import { NgRedux } from '@ptl/angular-redux-store';
import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { DRXRetrieveAllProductsItem } from '../../../../shared/interfaces/drx.interface';
import { DateService } from '../../../../shared/services/date.service';
import { DRXStateStructure } from '../../../medical-information/medication-information/reducers/drx-response.reducer';
import { MedicationStateStructure } from '../../../medical-information/medication-information/reducers/medication.reducer';
import { DrxService } from '../../../medical-information/medication-information/services/drx.service';
import { PlanAttributes, SummaryView, DrugDetails } from '../../interfaces/plan-attributes.interface';
import { Product } from '../../interfaces/product.interface';
import { PlanUtilityService } from '../../plan-summary-list/plan-utility.service';
import { MedicalDetailsModalComponent } from '../medical-details-modal/medical-details-modal.component';
import { PtlWindowLocationService } from '@ptl/global-components';

@Component({
    selector: 'app-senior-drug-coverage',
    templateUrl: './senior-drug-coverage.component.html',
    styleUrls: ['./senior-drug-coverage.component.scss'],
    standalone: false
})
export class SeniorDrugCoverageComponent implements OnInit{
  @Input() plan: Product<PlanAttributes, SummaryView>;
  @Input() addMedicationsUrl: string;

  drugDetails: DrugDetails = null;
  showDrugDataLoadingIndicator: boolean = false;

  constructor(
    private matDialog: MatDialog,
    private reducerUtilityService: ReducerUtilityService,
    private dateService: DateService,
    private drxService: DrxService,
    private windowService: PtlWindowLocationService
  ) {

  }
  ngOnInit() {
    const medState: MedicationStateStructure = this.reducerUtilityService.getState('MedicationInformation');
    if (!medState?.medicationForm?.selectedMedications?.length) {
      return;
    }

    this.showDrugDataLoadingIndicator = true;

    this.drxService.drxSessionData$.subscribe((returnedProducts) => {
      // If we receive an array with values, we can proceed to show the drug info
      if (returnedProducts?.length) {
        this.setDRXDataForPlanCard();
      }
      // An empty array means the service returned nothing, so we can hide the spinner
      else if (returnedProducts) {
        this.showDrugDataLoadingIndicator = false;
      }
    });
  }

  /**
   * @description checks against the reponses of DRX services in state, in order to display relevant info
   * I have this as a function and it's being used as a function in the template
   * because the data won't necessarily be available immediately...
   * @Todo - change to subscription and scoped variable
   */
   setDRXDataForPlanCard(): void {
    const drxData: DRXStateStructure = this.reducerUtilityService.getState('DrxServiceData');
    const medState: MedicationStateStructure = this.reducerUtilityService.getState('MedicationInformation');
    const totalMedsSelected = medState.medicationForm.selectedMedications.length;

    if (drxData.drxAllProductsResponse) {

      const matchedDrxPlan: DRXRetrieveAllProductsItem[] = drxData.drxAllProductsResponse.filter(drxPlan => {
        return drxPlan.PlanID === this.plan.attributes.PBP_ID && drxPlan.ContractID === this.plan.attributes.CMS_PLAN_ID;
      });

      if (matchedDrxPlan.length > 0) {
        let totalDrugsCovered = 0;
        matchedDrxPlan[0].FormularyTiers.filter(tierData => {
          totalDrugsCovered += tierData.CoveredDosages;
        });

        /** Determine which of the estimated costs is lower (full year or partial year) and choose that as your primary */
        let drxEstimateDrugCost = Number(
          matchedDrxPlan[0].EstimatedAnnualDrugCost > matchedDrxPlan[0].EstimatedAnnualDrugCostPartialYear
            ? matchedDrxPlan[0].EstimatedAnnualDrugCostPartialYear
            : matchedDrxPlan[0].EstimatedAnnualDrugCost
        );

        /** Subtract the Drug Premium value from the total estimate (reason unknown, possibly the covered costs by insurence) */
        const monthsRemainingForPlan = this.dateService.monthsRemainingForYear(this.plan.year);
        const drxDrugPremium = Number(matchedDrxPlan[0].DrugPremium) * monthsRemainingForPlan;
        drxEstimateDrugCost -= Number(drxDrugPremium);

        /** Determine the overall premium cost for the remainder of the year and add it to the estimate */
        let planPremiumCostsRemaining = 0;
        if (typeof this.plan.price === 'number') {
          planPremiumCostsRemaining = Number(this.plan.price * monthsRemainingForPlan);
        }
        drxEstimateDrugCost += planPremiumCostsRemaining;

        this.drugDetails = {
          HasDrugCoverage: matchedDrxPlan[0].HasDrugCoverage,
          EstimatedAnnualDrugCostPartialYear: Math.round(drxEstimateDrugCost),
          DosagesTotal: {
            covered: totalDrugsCovered,
            selected: totalMedsSelected
          }
        }
      }
      else {
        this.drugDetails = {
          HasDrugCoverage: false,
          EstimatedAnnualDrugCostPartialYear: 0,
          DosagesTotal: {
            covered: 0,
            selected: totalMedsSelected
          }
        };
      }
    }

    this.showDrugDataLoadingIndicator = false;
  }

  showMedicalDetails() {
    this.matDialog.open(MedicalDetailsModalComponent, {
      disableClose: false,
      maxWidth: '650px',
      maxHeight: '100vh',
      data: {
        type: 'Medication',
        seniorDrugCoverage: true,
        plan: this.plan
      }
    });
  }

  editPlanUsageFlow(): void {
    this.windowService.goToUrl(this.addMedicationsUrl);
  }
}
